import { type ComponentProps, Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import { ComboboxInput, type ComboboxPresetBaseProps, ComboboxRoot, type ComboboxRootProps } from './combobox';
import {
  type CreatableMarker,
  type CreatableProps,
  isCreatable,
  useCreatableHelpers,
  useCreatableItemEquality,
} from './combobox-creatable';
import {
  ComboboxInlineFrame,
  ComboboxPresetContent,
  ComboboxSelectionChip,
  type QueryProps,
} from './combobox-internals';
import { comboboxInlineInputStyles } from './combobox-styles';
import { useComboboxInputValue } from './use-combobox-input-value';

export type SingleComboboxProps<T> = ComboboxPresetBaseProps<T> &
  Pick<ComboboxRootProps<T, false>, 'isItemEqualToValue' | 'itemToStringLabel' | 'disabled'> &
  Omit<ComponentProps<typeof ComboboxInput>, 'value' | 'onValueChange' | 'children' | 'disabled'> &
  QueryProps &
  CreatableProps<T> & {
    /** The selected item, or `null` when nothing is selected. */
    value?: T | null;
    /** Fired with the newly selected item, or `null` when the selection is cleared. */
    onValueChange?: (value: T | null) => void;
    /** Whether a page is currently in-flight. Suppresses repeat `onLoadMore` fires while true, and drives the initial-fetch loading copy in the popup. */
    isLoading?: boolean;
    /** Whether more pages remain. When false, the tail probe is not rendered. */
    hasMore?: boolean;
    /** Fired when the user scrolls near the bottom of the loaded set. Detected via an IntersectionObserver on a 1px probe element after the rows. */
    onLoadMore?: () => void;
    /** Extra content rendered in the popup below the list, outside the filtered collection (e.g. `ComboboxActions`). */
    children?: ReactNode;
    /** Renders the option list in place under the input instead of in a popup, with the input kept for search. */
    inline?: boolean;
  };

export const SingleCombobox = <T,>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  getDescription,
  isItemEqualToValue,
  itemToStringLabel,
  emptyMessage,
  disabled,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  query,
  onQueryChange,
  onCreate,
  renderCreateOption,
  children,
  readOnly,
  inline = false,
  ...inputProps
}: SingleComboboxProps<T>) => {
  const isServerFilter = query !== undefined && onQueryChange !== undefined;
  const { typedQuery, handleInputValueChange, clearQuery } = useComboboxInputValue(
    isServerFilter ? onQueryChange : undefined
  );
  const [inFlightCreatable, setInFlightCreatable] = useState<T | null>(null);

  const { itemsForView, localFilter, stableItemToStringLabel } = useCreatableHelpers(
    items,
    getLabel,
    itemToStringLabel,
    onCreate,
    typedQuery ?? ''
  );

  const defaultIsItemEqualToValue = useCreatableItemEquality(getKey);
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  const showLoadingCopy = isLoading && !items?.length;

  const handleValueChange = useCallback<NonNullable<ComboboxRootProps<T, false>['onValueChange']>>(
    async (next) => {
      if (next && isCreatable(next) && onCreate) {
        setInFlightCreatable(next);

        try {
          const created = await onCreate((next as unknown as CreatableMarker).__create);
          onValueChange?.(created);
          setInFlightCreatable(null);
          clearQuery();
        } catch {
          // Keep the creatable sticky so the user can edit the query and retry.
        }

        return;
      }

      setInFlightCreatable(null);
      onValueChange?.(next);
    },
    [onCreate, onValueChange, clearQuery]
  );

  const rootValue = inFlightCreatable ?? value ?? null;
  const selectedLabel = rootValue !== null ? stableItemToStringLabel(rootValue) : '';
  const selectedKey = rootValue !== null ? String(getKey(rootValue)) : null;

  const handleClear = () => {
    setInFlightCreatable(null);
    onValueChange?.(null);
  };

  const Frame = inline ? ComboboxInlineFrame : Fragment;

  const previousSelectedKeyRef = useRef(selectedKey);
  useEffect(() => {
    // Clear transient search text when controlled state restores a committed selection.
    if (!inline && selectedKey !== null && previousSelectedKeyRef.current !== selectedKey) {
      clearQuery();
    }

    previousSelectedKeyRef.current = selectedKey;
  }, [clearQuery, selectedKey, inline]);

  return (
    <ComboboxRoot
      items={itemsForView}
      value={rootValue}
      onValueChange={handleValueChange}
      disabled={disabled}
      readOnly={readOnly}
      inline={inline}
      open={inline || undefined}
      // Inline, nothing is highlighted until the user arrows in, so Enter still submits the surrounding form.
      autoHighlight={!inline}
      loopFocus={!hasMore}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      inputValue={typedQuery ?? (inline ? '' : selectedLabel)}
      onInputValueChange={(next, details) => {
        if (inFlightCreatable !== null) {
          setInFlightCreatable(null);
        }
        handleInputValueChange(next, details.reason);
      }}
      filter={isServerFilter ? null : localFilter}
    >
      <Frame>
        <ComboboxInput
          disabled={disabled}
          readOnly={readOnly}
          {...inputProps}
          showTrigger={!inline && inputProps.showTrigger !== false}
          showClear={!inline && inputProps.showClear}
          className={cn(inline && comboboxInlineInputStyles, inputProps.className)}
        >
          {inline && rootValue !== null && (
            <ComboboxSelectionChip title={selectedLabel} onClear={disabled || readOnly ? undefined : handleClear}>
              {selectedLabel}
            </ComboboxSelectionChip>
          )}
        </ComboboxInput>
        <ComboboxPresetContent
          items={itemsForView}
          getKey={getKey}
          getLabel={getLabel}
          getDescription={getDescription}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          renderCreateOption={renderCreateOption}
          isServerFilter={isServerFilter}
          emptyMessage={emptyMessage}
          showLoadingCopy={showLoadingCopy}
          inline={inline}
        >
          {children}
        </ComboboxPresetContent>
      </Frame>
    </ComboboxRoot>
  );
};
